/**
 * Voice AI — Global Confirm Dialog Service
 * Promise-based: any component can `await confirmService.show(...)` to get true/false.
 * A single <voice-confirm-dialog> in app-shell.js listens and renders the modal.
 */

class ConfirmService {
  _resolve = null;
  _listener = null;

  /** Called once by <voice-confirm-dialog> to subscribe */
  onStateChange(callback) {
    this._listener = callback;
  }

  /**
   * Show a confirmation dialog. Returns a Promise<boolean>.
   * @param {Object} options
   * @param {string} options.title        — Modal title (e.g. "Delete Agent")
   * @param {string} [options.message]    — Bold primary text (e.g. 'Delete "Sunset Agent"?')
   * @param {string} [options.description]— Secondary muted text
   * @param {string} [options.icon]       — FA icon class (default: fa-trash)
   * @param {string} [options.iconColor]  — Icon color CSS
   * @param {string} [options.iconBg]     — Icon circle background CSS
   * @param {string} [options.confirmText]— Confirm button label (default: "Delete")
   * @param {string} [options.cancelText] — Cancel button label (default: "Cancel")
   * @param {string} [options.confirmClass]— Button class (default: "btn-danger")
   */
  show(options = {}) {
    // If a previous dialog is still pending, cancel it
    if (this._resolve) {
      this._resolve(false);
      this._resolve = null;
    }

    return new Promise(resolve => {
      this._resolve = resolve;
      this._listener?.({
        open: true,
        title: options.title || 'Confirm',
        message: options.message || '',
        description: options.description || '',
        icon: options.icon || 'fa-trash',
        iconColor: options.iconColor || 'var(--color-error)',
        iconBg: options.iconBg || 'rgba(239,68,68,0.1)',
        confirmText: options.confirmText || 'Delete',
        cancelText: options.cancelText || 'Cancel',
        confirmClass: options.confirmClass || 'btn-danger',
      });
    });
  }

  confirm() {
    this._resolve?.(true);
    this._resolve = null;
    this._listener?.({ open: false });
  }

  cancel() {
    this._resolve?.(false);
    this._resolve = null;
    this._listener?.({ open: false });
  }
}

export const confirmService = new ConfirmService();
